import { Divider, Tab, Tabs } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useTheme } from 'styled-components/macro';

import BannerMessages from '../BannerMessages/BannerMessages';
import NotificationsHistory from '../NotificationsHistory/NotificationsHistory';
import {
  ButtonCollapseStyled,
  FlexContainerStyled,
  HeaderStyled,
  HelpBtnIconStyled,
  LinkStyled,
  MainHeaderWrapStyled,
  MenuBtnStyled,
  MenuItemStyled,
  getPopupContentStyle,
} from './MainHeader.styled';
import Popup from 'components/Popup/Popup';
import UserInfo from 'components/UserInfo/UserInfo';
import { useOnboardingTour } from 'contexts/OnboardingTourContext';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { NoopT } from 'declarations/types/common';
import { DoubleArrowUpIcon, HelpIcon } from 'icons';

// Tab index <-> enum mappings to avoid hardcoded binary logic
const indexToTab: Record<number, ActiveTabTypeE> = {
  0: ActiveTabTypeE.Tickets,
  1: ActiveTabTypeE.Schedule,
  2: ActiveTabTypeE.Insights,
};

const tabToIndex: Record<ActiveTabTypeE, number> = {
  [ActiveTabTypeE.Tickets]: 0,
  [ActiveTabTypeE.Schedule]: 1,
  [ActiveTabTypeE.Insights]: 2,
};

type MainHeaderProps = {
  handleToggleSuggestedAdjustments: () => void;
  handleViewReports: () => void;
  isReportOpened: boolean;
  isSuggestedAdjustmentsOpened: boolean;
};

const getCollapseButtonTitle = (
  isScheduleTab: boolean,
  isSuggestedAdjustmentsOpened: boolean,
  isReportOpened: boolean
): string => {
  if (isScheduleTab) {
    return isSuggestedAdjustmentsOpened ? 'Hide Adjusted Schedule' : 'Show Adjusted Schedule';
  }
  return isReportOpened ? 'Hide report cards' : 'Show report cards';
};

const MainHeader: React.FC<MainHeaderProps> = ({
  handleToggleSuggestedAdjustments,
  handleViewReports,
  isReportOpened,
  isSuggestedAdjustmentsOpened,
}) => {
  /**
   * Hooks
   */
  const { handleStartTourClick } = useOnboardingTour();
  const { isMobile } = useResponsive();
  const menuPopupRef = useRef<PopupActions>(null);
  const theme = useTheme();
  const {
    activeTab,
    setActiveTab,
    ticketsTotalCount,
    getSearchParam,
    updateSearchParamsArray,
    scheduleCategoryId,
  } = useTickets();
  const [prevCategories, setPrevCategories] = useState<number[]>([]);
  const hasValidScheduleCategoryId = Number.isFinite(scheduleCategoryId) && scheduleCategoryId > 0;

  const isScheduleTab = activeTab === ActiveTabTypeE.Schedule;
  const isCollapseTargetOpened = isScheduleTab ? isSuggestedAdjustmentsOpened : isReportOpened;
  const handleCollapseClick = () => {
    if (isScheduleTab) {
      handleToggleSuggestedAdjustments();
    } else {
      handleViewReports();
    }
  };

  /** Handlers */
  const handleMenuItemClick = useCallback(
    (cb: NoopT) => () => {
      menuPopupRef.current?.close();
      cb();
    },
    []
  );

  const handleChangeTab = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      const newTab = indexToTab[newValue] ?? ActiveTabTypeE.Tickets;

      setActiveTab(newTab);
      sessionStorage.setItem('activeTab', newTab);

      const categoryParam = getSearchParam(TicketsRefinementKeysE.Category);
      const currentCategories: number[] = categoryParam
        ? categoryParam
            .split(',')
            .map(c => Number(c.trim()))
            .filter(n => !isNaN(n))
        : [];

      if (newTab === ActiveTabTypeE.Schedule) {
        const filtered = currentCategories.filter(c => c !== scheduleCategoryId);
        setPrevCategories(filtered);
        updateSearchParamsArray(
          TicketsRefinementKeysE.Category,
          hasValidScheduleCategoryId ? [scheduleCategoryId] : null
        );
      } else {
        // Tickets & Insights: restore previous categories and ensure schedule category is not included
        const categoriesWithoutSchedule = prevCategories.filter(c => c !== scheduleCategoryId);
        updateSearchParamsArray(
          TicketsRefinementKeysE.Category,
          categoriesWithoutSchedule.length > 0 ? categoriesWithoutSchedule : null
        );
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
        updateSearchParamsArray(TicketsRefinementKeysE.RequestSubCategoryIds, null);
        updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
      }
    },
    [
      setActiveTab,
      getSearchParam,
      updateSearchParamsArray,
      scheduleCategoryId,
      prevCategories,
      hasValidScheduleCategoryId,
    ]
  );

  const currentTabIndex = tabToIndex[activeTab] ?? 0;

  /** Effects */
  useEffect(() => {
    const storedTab = sessionStorage.getItem('activeTab');
    const isValid =
      storedTab === ActiveTabTypeE.Tickets ||
      storedTab === ActiveTabTypeE.Schedule ||
      storedTab === ActiveTabTypeE.Insights;
    setActiveTab(isValid ? (storedTab as ActiveTabTypeE) : ActiveTabTypeE.Tickets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When Schedule tab is active, ensure category is set to schedule only
  useEffect(() => {
    if (activeTab !== ActiveTabTypeE.Schedule || !hasValidScheduleCategoryId) return;
    const categoryParam = getSearchParam(TicketsRefinementKeysE.Category);
    const currentCategories: number[] = categoryParam
      ? categoryParam
          .split(',')
          .map(c => Number(c.trim()))
          .filter(n => !isNaN(n))
      : [];
    if (!currentCategories.includes(scheduleCategoryId)) {
      updateSearchParamsArray(TicketsRefinementKeysE.Category, [scheduleCategoryId]);
    }
  }, [
    activeTab,
    scheduleCategoryId,
    hasValidScheduleCategoryId,
    getSearchParam,
    updateSearchParamsArray,
  ]);

  return (
    <MainHeaderWrapStyled className="dealer-report">
      <HeaderStyled isReportOpened={isCollapseTargetOpened}>
        <Tabs value={currentTabIndex} onChange={handleChangeTab}>
          <Tab label={`Tickets ${ticketsTotalCount ?? '...'}`} />
          <Tab label="Schedule" className="schedule-tab" />
          <Tab label="Insights" className="insights-tab" />
        </Tabs>
        <FlexContainerStyled>
          <HeaderStyled isReportOpened={isCollapseTargetOpened}>
            <FlexContainerStyled>
              <NotificationsHistory />
              <Popup
                offsetY={4}
                arrow={false}
                ref={menuPopupRef}
                position="bottom right"
                contentStyle={getPopupContentStyle(theme, isMobile)}
                trigger={
                  <HelpBtnIconStyled
                    title="Start onboarding tour/show release notes"
                    icon={<HelpIcon />}
                  />
                }
              >
                <ul>
                  <MenuItemStyled>
                    <MenuBtnStyled
                      variant="text"
                      onClick={handleMenuItemClick(handleStartTourClick)}
                    >
                      Onboarding tour
                    </MenuBtnStyled>
                  </MenuItemStyled>

                  <MenuItemStyled>
                    <LinkStyled to={RoutePathsE.RELEASES_PAGE}>Release notes</LinkStyled>
                  </MenuItemStyled>
                </ul>
              </Popup>
              <UserInfo iconOnly={true} />
              <ButtonCollapseStyled
                size="xs"
                variant="outline"
                className="collapse-report-btn"
                onClick={handleCollapseClick}
                isReportOpened={isCollapseTargetOpened}
                icon={<DoubleArrowUpIcon />}
                title={getCollapseButtonTitle(
                  isScheduleTab,
                  isSuggestedAdjustmentsOpened,
                  isReportOpened
                )}
              >
                Collapse
              </ButtonCollapseStyled>
            </FlexContainerStyled>
          </HeaderStyled>
        </FlexContainerStyled>
      </HeaderStyled>
      <Divider />
      {activeTab !== ActiveTabTypeE.Insights && <BannerMessages />}
    </MainHeaderWrapStyled>
  );
};

export default MainHeader;
